<template>
  <div class="container">
    <!-- Loading Indicator -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-spinner"></div>
      <p>{{ loadingMessage }}</p>
    </div>

    <!-- Semester Selector -->
    <div class="semester-selector">
      <SemesterSelector v-model="semesterId" placeholder="Select Semester" />
    </div>
    <div class="status-selector">
      <el-select v-model="status" placeholder="Select Status" clearable>
        <el-option v-for="item in statusOptions" :key="item.value" :label="item.label" :value="item.value" />
      </el-select>
    </div>

    <!-- Course List -->
    <div class="course-list" v-if="!isLoading">
      <div v-if="signups.length === 0" class="empty-state">
        <!-- <img src="../assets/images/no-courses.png" alt="No courses" /> -->
        <p>No courses found for this semester</p>
      </div>
      
      <div v-else class="course-cards">
        <div v-for="signup in signups" :key="signup.id" class="course-card">
          <div class="course-header">
            <h3>{{ signup.courseName }}</h3>
            <el-tag :type="getStatusColor(signup)" size="small">
              {{ getStatusName(signup) }}
            </el-tag>
          </div>
          
          <div class="course-details">
            <div class="detail-item">
              <i class="el-icon-folder"></i>
              <span>{{ signup.categoryName }}</span>
            </div>
            <div class="detail-item">
              <i class="el-icon-date"></i>
              <span>{{ new Date(signup.updateAt).toLocaleString() }}</span>
            </div>
          </div>
          
          <div class="course-actions">
            <el-button type="primary" size="small" @click="viewCourseDetails(signup)">
              View Details
            </el-button>
            <el-button type="danger" size="small" @click="cancelSignup(signup)">
              Cancel
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Pull to Refresh -->
    <div class="pull-to-refresh" v-show="!isLoading">
      <p v-if="isRefreshing">Refreshing...</p>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { BACKEND_URL } from '../../../share-source/src/config/config.js';
import SemesterSelector from '../components/SemesterSelector.vue';
import { useUserStore } from '../stores/Global.js';
import { SignupStatus } from '../constants/SignupStatus';

export default {
  name: 'MyCourse',
  components: {
    SemesterSelector
    
  },
  setup() {
    const userStore = useUserStore();
    return {
      userStore,
      SignupStatus
    }
  },
  data() {
    return {
      signups: [],
      semesterId: null,
      isLoading: false,
      loadingMessage: 'Loading your courses...',
      isRefreshing: false,
      status: null
    }
  },
  computed: {
    statusOptions() {
      return Object.values(SignupStatus).filter(status => typeof status === 'number').map(status => ({
        value: status,
        label: SignupStatus.getKey(status)
      }));
    }
  },
  watch: {
    semesterId() {
      this.fetchSignups();
    },
    status() {
      this.fetchSignups();
    },
    '$route.params.status': {
      immediate: true,
      handler(newStatus) {
        if (newStatus) {
          this.status = parseInt(newStatus);
        } else {
          this.status = null;
        }
      }
    }
  },
  mounted() {
    this.fetchSignups();
  },
  methods: {
    getStatusColor(signup) {
      if (signup.status === SignupStatus.PAID) {
        return 'success';
      } else if (signup.status === SignupStatus.PAYABLE) {
        return 'warning';
      } else if (signup.status === SignupStatus.CREATED) {
        return 'primary';
      } else if (signup.status === SignupStatus.EXPIRED) {
        return 'danger';
      } else if (signup.status === SignupStatus.CANCELLED) {
        return 'danger';
      } else if (signup.status === SignupStatus.SIGNED_UP) {
        return 'info';
      } else if (signup.status === SignupStatus.TRANSFERRED) {
        return 'warning';
      } else if (signup.status === SignupStatus.REFUNDED) {
        return 'danger';
      }
    },
    getStatusName(signup) {
      let statusName = SignupStatus.getKey(signup.status);
      if (statusName === 'SIGNED_UP') {
        if (signup.interviewStatus < 0) {
          return 'Fail Interview';
        } else if (signup.lotteryStatus < 0) {
          return 'Fail Lottery';
        }
      }
      return statusName;
    },
    async fetchSignups() {
      this.isLoading = true;
      try {
        const studentId = this.userStore.getStudentId();
        const params = new URLSearchParams();
        if (this.semesterId) {
          params.append('semesterId', this.semesterId);
        }
        if (this.status) {
          params.append('status', this.status);
        }

        const response = await axios.get(`${BACKEND_URL}/api/signup/student/${studentId}`, { params });
        this.signups = response.data;
      } catch (error) {
        console.error("Error fetching signups:", error);
        this.$message.error('Failed to load your courses');
        this.signups = [];
      } finally {
        this.isLoading = false;
      }
    },
    async cancelSignup(signup) {
      try {
        await this.$confirm('Are you sure you want to cancel this course?', 'Cancel Course', {
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          type: 'warning'
        });
        
        this.isLoading = true;
        await axios.delete(`${BACKEND_URL}/api/signup/delete/${signup.id}`);
        this.signups = this.signups.filter(s => s.id !== signup.id);
        this.$message.success('Course cancelled successfully');
      } catch (error) {
        if (error !== 'cancel') {
          console.error("Error cancelling signup:", error);
          this.$message.error('Failed to cancel course');
        }
      } finally {
        this.isLoading = false;
      }
    },
    viewCourseDetails(signup) {
      // Navigate to course details page
      this.$router.push({
        name: 'SignupViewByCourse',
        params: { courseId: signup.courseId }
      });
    }
  }
}
</script>

<style scoped>
.container {
  /* padding: 15px; */
  /* min-height: 100vh; */
  display: flex;
  flex-direction: column;
  overflow: auto;
  background-color: #f5f7fa;
}

.semester-selector {
  /* margin-bottom: 4px; */
  background: white;
  /* padding: 15px; */
  /* border-radius: 10px; */
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.course-list {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
}

.empty-state img {
  width: 120px;
  height: 120px;
  margin-bottom: 20px;
}

.empty-state p {
  color: #909399;
  font-size: 16px;
}

.course-cards {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.course-card {
  background: white;
  border-radius: 10px;
  padding: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.course-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.course-header h3 {
  margin: 0;
  font-size: 16px;
  color: #303133;
}

.course-details {
  margin: 10px 0;
}

.detail-item {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  color: #606266;
  font-size: 14px;
}

.detail-item i {
  margin-right: 8px;
  color: #909399;
}

.course-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 15px;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #409EFF;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.pull-to-refresh {
  text-align: center;
  padding: 10px;
  color: #909399;
  font-size: 14px;
}
</style> 